import { useFeedHistory } from "../../api/hooks.js";
import type { Airing, Decision, NowPlaying } from "../../api/types.js";
import { Panel } from "../../design-system/surfaces.js";
import { formatClock, formatDuration } from "../../lib/format.js";
import { useNow } from "../../lib/use-now.js";
import { Artwork, LiveBars } from "../../player/artwork.js";
import { AiredProgress } from "../../player/transport.js";

interface PlaybackTimelineProps {
  feedId: string;
  live: NowPlaying | null;
  liveError: string | null;
  liveLoading: boolean;
  planError: string | null;
  planLoading: boolean;
  planned: Decision[];
}

/**
 * One playback chronology. The current item, the decisions that follow it and the
 * items that preceded it are states of the same sequence, so they share one list
 * rather than making the operator reconcile separate surfaces.
 */
export function PlaybackTimeline({
  feedId,
  live,
  liveError,
  liveLoading,
  planError,
  planLoading,
  planned,
}: PlaybackTimelineProps) {
  const airing = live?.nowPlaying ?? null;
  const history = useFeedHistory(
    feedId,
    airing?.historyId ?? null,
    feedId !== "",
  );
  const recent = (history.data ?? []).filter(
    (item) => item.historyId !== airing?.historyId,
  );
  const now = useNow(1000, airing !== null);

  return (
    <Panel className="overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-5 py-4">
        <h2 className="text-sm font-semibold text-ink">Playback</h2>
        <span className="font-mono text-[0.6875rem] text-ink-3">
          {planned.length > 0 ? `${String(planned.length)} planned` : ""}
        </span>
      </div>

      <ol className="divide-y divide-border border-t border-border">
        <NowPlayingRow
          airing={airing}
          error={liveError}
          loading={liveLoading}
          now={now}
          snapshot={live}
        />

        {planLoading && planned.length === 0 ? (
          <TimelineMessage label="Next">Reading the plan…</TimelineMessage>
        ) : planError && planned.length === 0 ? (
          <TimelineMessage label="Next">
            The plan for this feed could not be read.
          </TimelineMessage>
        ) : planned.length === 0 ? (
          <TimelineMessage label="Next">
            Nothing is planned for this feed yet.
          </TimelineMessage>
        ) : (
          planned
            .slice(0, 8)
            .map((decision, index) => (
              <UpcomingRow
                decision={decision}
                index={index}
                key={decision.id}
              />
            ))
        )}

        <li className="bg-bg-2/50 px-5 py-2 font-mono text-[0.5625rem] tracking-[0.14em] text-ink-3 uppercase">
          Recently played
        </li>

        {history.loading && recent.length === 0 ? (
          <TimelineMessage label="Past">
            Reading this feed&apos;s history…
          </TimelineMessage>
        ) : history.error && recent.length === 0 ? (
          <TimelineMessage label="Past">
            This feed&apos;s history could not be read.
          </TimelineMessage>
        ) : recent.length === 0 ? (
          <TimelineMessage label="Past">
            Nothing has aired on this feed yet.
          </TimelineMessage>
        ) : (
          recent.map((item) => <RecentRow item={item} key={item.historyId} />)
        )}
      </ol>
    </Panel>
  );
}

function NowPlayingRow({
  airing,
  error,
  loading,
  now,
  snapshot,
}: {
  airing: Airing | null;
  error: string | null;
  loading: boolean;
  now: number;
  snapshot: NowPlaying | null;
}) {
  return (
    <li className="bg-[color-mix(in_oklch,var(--accent)_5%,var(--surface))] px-4 py-4 sm:px-5">
      <div className="flex items-center gap-3">
        <span className="w-8 shrink-0 font-mono text-[0.625rem] font-medium tracking-[0.08em] text-accent-text uppercase">
          Now
        </span>
        <Artwork airing={airing} className="size-11" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {airing ? <LiveBars className="h-2.5 text-accent-text" /> : null}
            <div className="truncate text-sm font-medium text-ink">
              {loading
                ? "…"
                : (airing?.title ??
                  (error ? "Not reachable" : "Nothing airing"))}
            </div>
          </div>
          {airing?.artist ? (
            <div className="mt-0.5 truncate text-xs text-ink-3">
              {airing.artist}
            </div>
          ) : null}
          {airing ? (
            <AiredProgress
              className="mt-2 max-w-sm"
              now={now}
              snapshot={snapshot}
              withTimes
            />
          ) : null}
        </div>
      </div>
    </li>
  );
}

function UpcomingRow({
  decision,
  index,
}: {
  decision: Decision;
  index: number;
}) {
  return (
    <li className="flex items-center gap-3 px-4 py-2.5 sm:px-5">
      <span className="w-8 shrink-0 font-mono text-[0.6875rem] text-ink-3 tabular-nums">
        {String(index + 1)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm text-ink">{decision.title}</div>
        {decision.artist ? (
          <div className="truncate text-xs text-ink-3">{decision.artist}</div>
        ) : null}
      </div>
      <span className="shrink-0 font-mono text-[0.625rem] tracking-[0.04em] text-ink-3 uppercase">
        {decision.source}
      </span>
      {decision.plannedStartAt ? (
        <span className="hidden w-14 shrink-0 text-right font-mono text-[0.6875rem] text-ink-3 tabular-nums sm:block">
          {formatClock(decision.plannedStartAt)}
        </span>
      ) : null}
    </li>
  );
}

function RecentRow({ item }: { item: Airing }) {
  return (
    <li className="flex items-center gap-3 px-4 py-2.5 sm:px-5">
      <span className="w-8 shrink-0 font-mono text-[0.625rem] text-ink-3">
        Past
      </span>
      <Artwork airing={item} className="size-9 opacity-75" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm text-ink-2">{item.title}</div>
        {item.artist ? (
          <div className="truncate text-xs text-ink-3">{item.artist}</div>
        ) : null}
      </div>
      <span className="shrink-0 font-mono text-[0.6875rem] text-ink-3 tabular-nums">
        {formatDuration(item.durationMs)}
      </span>
      <span className="hidden w-16 shrink-0 text-right font-mono text-[0.6875rem] text-ink-3 tabular-nums sm:block">
        {formatClock(item.startedAt)}
      </span>
    </li>
  );
}

function TimelineMessage({
  children,
  label,
}: {
  children: string;
  label: string;
}) {
  return (
    <li className="flex items-center gap-3 px-4 py-3 text-sm text-ink-3 sm:px-5">
      <span className="w-8 shrink-0 font-mono text-[0.625rem] uppercase">
        {label}
      </span>
      <span>{children}</span>
    </li>
  );
}
